import { useAuth, useUser } from "@clerk/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  addDays,
  addMonths,
  intervalToDuration,
  Duration,
  endOfMonth,
  differenceInMinutes,
  endOfDay,
} from "date-fns";
import { clientRPC } from "@/utils/hono-client";
import { useMemo } from "react";

const MAX_BEAST_MONTHLY_CREDITS = 3000;
const MAX_ROOKIE_DAILY_CREDITS = 50;

type UserPlan = "veteran" | "master" | "wizard" | "beast" | "basic";

type UseUserAndCreditsReturn =
  | {
      isLoading: true;
      isError: false;
      isLoggedIn: undefined;
      user: undefined;
      plan: undefined;
      isUnlimited: undefined;
      hasUpgraded: undefined;
      maxDailyCredits: undefined;
      maxMonthlyCredits: undefined;
      usedCredits: undefined;
      remainingCredits: undefined;
      nextCreditRenewal: undefined;
      timeToNextRenewal: undefined;
    }
  | {
      isLoading: false;
      isError: true;
      isLoggedIn: undefined;
      user: undefined;
      plan: undefined;
      isUnlimited: undefined;
      hasUpgraded: undefined;
      maxDailyCredits: undefined;
      maxMonthlyCredits: undefined;
      remainingCredits: undefined;
      usedCredits: undefined;
      nextCreditRenewal: undefined;
      timeToNextRenewal: undefined;
    }
  | {
      isLoading: false;
      isError: false;
      isLoggedIn: false;
      user: undefined;
      plan: undefined;
      isUnlimited: undefined;
      hasUpgraded: undefined;
      maxDailyCredits: undefined;
      maxMonthlyCredits: undefined;
      remainingCredits: undefined;
      usedCredits: undefined;
      nextCreditRenewal: undefined;
      timeToNextRenewal: undefined;
    }
  | {
      isLoading: false;
      isError: false;
      isLoggedIn: true;
      user: ReturnType<typeof useUser>["user"];
      plan: Exclude<UserPlan, "basic" | "beast">;
      isUnlimited: true;
      hasUpgraded: true;
      maxDailyCredits: undefined;
      maxMonthlyCredits: undefined;
      usedCredits: number;
      remainingCredits: number;
      nextCreditRenewal: undefined;
      timeToNextRenewal: undefined;
    }
  | {
      isLoading: false;
      isError: false;
      isLoggedIn: true;
      user: ReturnType<typeof useUser>["user"];
      plan: "beast";
      isUnlimited: false;
      hasUpgraded: true;
      maxDailyCredits: undefined;
      maxMonthlyCredits: number;
      usedCredits: number;
      remainingCredits: number;
      nextCreditRenewal: Date;
      timeToNextRenewal: Duration;
    }
  | {
      isLoading: false;
      isError: false;
      isLoggedIn: true;
      user: ReturnType<typeof useUser>["user"];
      plan: "basic";
      isUnlimited: false;
      hasUpgraded: false;
      maxDailyCredits: number;
      maxMonthlyCredits: undefined;
      usedCredits: number;
      remainingCredits: number;
      nextCreditRenewal: Date;
      timeToNextRenewal: Duration;
    };

export const fetchUsage = async (): Promise<{ data: number }> => {
  const response = await clientRPC.api["user-usage"].$get();
  if (!response.ok) {
    throw new Error("Failed to fetch usage");
  }
  return response.json();
};

export function getNextCreditRenewal(plan: "beast"): Date;
export function getNextCreditRenewal(plan: "basic"): Date;
export function getNextCreditRenewal(plan: UserPlan): Date | undefined;
export function getNextCreditRenewal(plan: UserPlan): Date | undefined {
  const now = new Date();

  if (plan === "beast") {
    return addMonths(Date.UTC(now.getUTCFullYear(), now.getUTCMonth()), 1);
  } else if (plan === "basic") {
    return addDays(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
      1
    );
  } else {
    return undefined;
  }
}

export function getMissingTimeToCreditsRenewal(plan: "beast"): Duration;
export function getMissingTimeToCreditsRenewal(plan: "basic"): Duration;
export function getMissingTimeToCreditsRenewal(
  plan: UserPlan
): Duration | undefined;
export function getMissingTimeToCreditsRenewal(plan: UserPlan) {
  const now = new Date();
  const utcNow = new Date(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds()
  );

  let nextRenewal = getNextCreditRenewal(plan);
  if (!nextRenewal) {
    return undefined;
  }

  const duration = intervalToDuration({ start: utcNow, end: nextRenewal });

  return duration;
}

export function formatDurationRemainingToCreditsRenewal(
  duration: Duration,
  numberOfUnits: number
) {
  const unitOrder = ["years", "months", "days", "hours", "minutes"] as const;

  let unitCount = 0;
  let outputString = "";

  for (const unit of unitOrder) {
    if ((duration[unit] ?? 0) > 0) {
      unitCount += 1;
      outputString = outputString.concat(`${duration[unit]}${unit[0]}`);
    }
    if (unitCount >= numberOfUnits) {
      break;
    } else {
      outputString = outputString.concat(" ");
    }
  }
  return outputString;
}

export function timeProgressPercentToCreditsRenewal(plan: UserPlan): number {
  const now = new Date();
  const utcNow = new Date(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    now.getUTCHours(),
    now.getUTCMinutes(),
    now.getUTCSeconds()
  );

  if (plan === "beast") {
    const endMonth = endOfMonth(utcNow);

    const minutesInMonth = endMonth.getDay() * 24 * 60;
    const minutesRemaining = differenceInMinutes(endMonth, utcNow);

    return ((minutesInMonth - minutesRemaining) / minutesInMonth) * 100;
  } else if (plan === "basic") {
    const endDay = endOfDay(utcNow);
    const minutesInDay = 24 * 60;

    const minutesRemaining = differenceInMinutes(endDay, utcNow);
    return ((minutesInDay - minutesRemaining) / minutesInDay) * 100;
  } else {
    return 0;
  }
}

export function useUserAndCredits(): UseUserAndCreditsReturn {
  const userInfo = useUser();
  const { isSignedIn } = useAuth();

  const {
    data,
    isLoading: isUsageLoading,
    isError: isUsageError,
  } = useQuery({
    queryKey: ["user-usage"],
    queryFn: fetchUsage,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: isSignedIn,
  });

  const creditsUsed = data?.data ?? 0;
  const plan = (userInfo.user?.publicMetadata["status"] ?? "basic") as UserPlan;

  const returnValue = useMemo<UseUserAndCreditsReturn>(() => {
    if (!userInfo.isLoaded || isUsageLoading) {
      return {
        isLoading: true,
        isError: false,
        isLoggedIn: undefined,
        user: undefined,
        plan: undefined,
        isUnlimited: undefined,
        hasUpgraded: undefined,
        maxDailyCredits: undefined,
        maxMonthlyCredits: undefined,
        remainingCredits: undefined,
        usedCredits: undefined,

        nextCreditRenewal: undefined,
        timeToNextRenewal: undefined,
      };
    }

    if (isUsageError) {
      return {
        isLoading: false,
        isError: true,
        isLoggedIn: undefined,
        user: undefined,
        plan: undefined,
        isUnlimited: undefined,
        hasUpgraded: undefined,
        maxDailyCredits: undefined,
        maxMonthlyCredits: undefined,
        remainingCredits: undefined,
        usedCredits: undefined,

        nextCreditRenewal: undefined,
        timeToNextRenewal: undefined,
      };
    }

    if (!userInfo.isSignedIn) {
      return {
        isLoading: false,
        isLoggedIn: false,
        user: undefined,
        plan: undefined,
        isUnlimited: undefined,
        hasUpgraded: undefined,
        maxDailyCredits: undefined,
        maxMonthlyCredits: undefined,
        isError: false,
        usedCredits: undefined,
        remainingCredits: undefined,
        nextCreditRenewal: undefined,
        timeToNextRenewal: undefined,
      };
    }

    if (plan === "veteran" || plan === "master" || plan === "wizard") {
      return {
        isLoading: false,
        isLoggedIn: true,
        isError: false,
        user: userInfo.user,
        plan,
        isUnlimited: true,
        hasUpgraded: true,
        maxDailyCredits: undefined,
        maxMonthlyCredits: undefined,
        usedCredits: creditsUsed,
        remainingCredits: Infinity,
        nextCreditRenewal: undefined,
        timeToNextRenewal: undefined,
      };
    }

    if (plan === "beast") {
      return {
        isLoading: false,
        isLoggedIn: true,
        isError: false,
        user: userInfo.user,
        plan: "beast",
        isUnlimited: false,
        hasUpgraded: true,
        maxDailyCredits: undefined,
        usedCredits: creditsUsed,
        maxMonthlyCredits: MAX_BEAST_MONTHLY_CREDITS,
        remainingCredits: MAX_BEAST_MONTHLY_CREDITS - creditsUsed,
        nextCreditRenewal: getNextCreditRenewal(plan),
        timeToNextRenewal: getMissingTimeToCreditsRenewal(plan),
      };
    }

    return {
      isLoading: false,
      isLoggedIn: true,
      user: userInfo.user,
      plan: "basic",
      isUnlimited: false,
      hasUpgraded: false,
      maxDailyCredits: MAX_ROOKIE_DAILY_CREDITS,
      maxMonthlyCredits: undefined,
      isError: false,
      usedCredits: creditsUsed,
      remainingCredits: MAX_ROOKIE_DAILY_CREDITS - creditsUsed,
      nextCreditRenewal: getNextCreditRenewal(plan),
      timeToNextRenewal: getMissingTimeToCreditsRenewal(plan),
    };
  }, [
    userInfo.isLoaded,
    userInfo.isSignedIn,
    isUsageLoading,
    isUsageError,
    creditsUsed,
    userInfo.user,
    plan,
  ]);

  return returnValue;
}
